import { useEffect, useRef, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0ActionItem, F0Button } from '@factorialco/f0-react'
import { NotesTextEditorHandle, OneEmptyState } from '@factorialco/f0-react/dist/experimental'
import { Cross } from '@factorialco/f0-react/icons/app'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { MeetingsMeetingGenerateChatDocument } from 'generated/resources/graphql'
import { v4 as uuidv4 } from 'uuid'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import 'modules/meetings/lib/utils/index.styles.css'

import { AiChatMessage } from './components/AiChatMessage'
import { BlankState, BlankStatePromptTypes } from './components/BlankState'
import { MessageInput } from './components/MessageInput'

interface AiChatProps {
  meetingId?: string
  textEditorRef?: React.RefObject<NotesTextEditorHandle> | null
  close?: () => void
  otherAttendeeFirstname?: string
  blankStateType?: BlankStatePromptTypes
  isOverview?: boolean
}

interface ChatMessage {
  message: string
  role: 'user' | 'assistant'
  rawMessage?: string
  isError?: boolean
}

interface ChatHistory {
  messages: ChatMessage[]
  sources: string
}

export const AiChat = ({
  meetingId,
  textEditorRef,
  close,
  otherAttendeeFirstname,
  blankStateType,
  isOverview = false,
}: AiChatProps) => {
  const [isLoading, setIsLoading] = useState(false)
  const [isCompleting, setIsCompleting] = useState(false)
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const [isScrolledFromTop, setIsScrolledFromTop] = useState(false)
  const [threadId, setThreadId] = useState(() => uuidv4())

  const storageKey: string = LOCAL_STORAGE_KEYS.meetingOneOnOneChatHistory(
    isOverview ? 'overview' : meetingId?.toString() || ''
  )

  const [chatHistory, setChatHistory] = useLocalStorage<ChatHistory>(storageKey, {
    messages: [],
    sources: '',
  })

  const [generateMeetingsChat] = useMutation(MeetingsMeetingGenerateChatDocument, {
    extractorFn: (data) => data.meetingsMutations.generateMeetingsChat,
  })

  const handleSendMessage = async (message: string) => {
    if (isLoading || !meetingId) return

    setIsLoading(true)

    const currentHistory = chatHistory || { messages: [], sources: '' }
    const newUserMessage: ChatMessage = { message, role: 'user', isError: false }
    const updatedMessages = [...currentHistory.messages, newUserMessage]

    setChatHistory({ messages: updatedMessages, sources: currentHistory.sources })

    const messageHistory = updatedMessages.map((msg) => msg.message)
    const roleHistory = updatedMessages.map((msg) => msg.role)

    const result = await generateMeetingsChat({
      variables: {
        meetingId,
        messageHistory,
        roleHistory,
        threadId,
      },
    })

    const handler = handleResult(result, () => null)

    let finalMessages = updatedMessages
    let finalSources = currentHistory.sources

    if (handler?.success) {
      const assistantMessage: ChatMessage = {
        message: handler?.data?.chat?.lastMessage || '',
        rawMessage: handler?.data?.chat?.rawLastMessage ?? undefined,
        role: 'assistant',
        isError: false,
      }
      finalMessages = [...updatedMessages, assistantMessage]
      // Update sources with the latest from backend if available
      finalSources = handler?.data?.chat?.sources || currentHistory.sources
    } else {
      const errorMessage: ChatMessage = {
        message: i18n.t('meetings.meeting.chat.error_generating_chat'),
        role: 'assistant',
        isError: true,
      }
      finalMessages = [...updatedMessages, errorMessage]
    }

    setIsCompleting(true)
    setIsLoading(false)

    setTimeout(() => {
      setChatHistory({ messages: finalMessages, sources: finalSources })
      setIsCompleting(false)
    }, 500)
  }

  const handleScroll = () => {
    if (messagesContainerRef.current) {
      const { scrollTop } = messagesContainerRef.current
      setIsScrolledFromTop(scrollTop > 10)
    }
  }

  useEffect(() => {
    const container = messagesContainerRef.current
    if (container) {
      container.addEventListener('scroll', handleScroll)
      return () => container.removeEventListener('scroll', handleScroll)
    }
  }, [])

  useEffect(() => {
    if (messagesContainerRef.current && chatHistory?.messages.length) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: 'smooth',
      })
    }
  }, [chatHistory])

  return (
    <Box flexDirection="column" height="full" position="relative" overflowY="auto">
      {close && (
        <Box
          position="absolute"
          width="full"
          alignItems="flexEnd"
          padding="s12"
          border={
            isScrolledFromTop
              ? {
                  bottom: {
                    width: 's1',
                    style: 'solid',
                    color: 'grey200',
                  },
                }
              : undefined
          }
          style={{
            top: '0',
            left: '0',
            background: 'rgba(255,255,255,0.5)',
            backdropFilter: 'blur(16px)',
          }}
        >
          <F0Button
            label={i18n.t('common.close')}
            onClick={close}
            variant="ghost"
            hideLabel
            icon={Cross}
          />
        </Box>
      )}
      {meetingId ? (
        <>
          <Box
            paddingX="s16"
            paddingBottom="s48"
            ref={messagesContainerRef}
            flexGrow
            flexDirection="column"
            overflowY="auto"
            className={'utils_hiddenScrollBar'}
            style={{
              paddingTop: close ? '56px' : '16px',
              scrollBehavior: 'smooth',
            }}
          >
            {chatHistory && chatHistory.messages.length === 0 && (
              <BlankState
                employeeName={otherAttendeeFirstname || ''}
                onPromptClick={handleSendMessage}
                type={blankStateType}
              />
            )}
            {chatHistory?.messages.map((chat, index) => (
              <AiChatMessage
                key={index}
                isAssistant={chat.role === 'assistant'}
                content={chat.message}
                textEditorRef={textEditorRef}
                isError={chat.isError}
              />
            ))}
            {(isLoading || isCompleting) && (
              <F0ActionItem
                status={isCompleting ? 'completed' : 'executing'}
                title={
                  isCompleting
                    ? i18n.t('meetings.meeting.chat.completed')
                    : i18n.t('meetings.meeting.chat.writing')
                }
              />
            )}
          </Box>

          <Box flexNoShrink position="relative" paddingX="s8" paddingBottom="s8" gap="s8">
            <MessageInput
              clearChat={
                !isLoading && !isCompleting && chatHistory && chatHistory.messages.length > 0
                  ? () => {
                      setChatHistory({ messages: [], sources: '' })
                      setThreadId(uuidv4())
                    }
                  : undefined
              }
              disabled={isLoading || isCompleting}
              placeholder={i18n.t('meetings.meeting.chat.write_message_placeholder')}
              onSend={handleSendMessage}
              isSending={isLoading}
              sources={chatHistory?.sources}
            />
          </Box>
        </>
      ) : (
        <Box padding="s16" width="full" height="full" alignItems="center" justifyContent="center">
          <OneEmptyState
            description={i18n.t('meetings.meeting.chat.no_meetings_programmed_description')}
            emoji="🗓️"
            title={i18n.t('meetings.meeting.chat.no_meetings_programmed')}
          />
        </Box>
      )}
    </Box>
  )
}
